import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { Button } from "devextreme-react/button";
import FormField from "../../dynamicFormBuilder/FormField";
import { DetailAccountingLayout } from "./Layout";
import { loadLookupFromDataSource, GetFormatedDateWithTime } from "../../common/Utils";
import { CONCURRENCY_HANDLED, getApiErrorMessage } from "../../common/concurrency";
import data from "../../assets/apiRoutes.json";
import { EMPTY_GUID } from "../../const/constants";

const ROUTES = data.IM.ItemManagement;

// Details -> "Accounting" sub-tab (Delphi uIMD0019 Details/Accounting): GL dimensions + vendor/asset/date
// for the SELECTED detail line. Editable; Save persists the line via EditTransactionDetail and hands the
// refreshed row back so the grid + token stay in sync.
function DetailAccountingTab(props) {
    const currentUser = props.oidc?.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const accessToken = props.oidc?.user?.access_token;

    const line = props.formFields || {};
    const hasLine = line.rowid && line.rowid !== EMPTY_GUID;
    const editable = hasLine && !props.disabled;

    const finalLayout = useMemo(() => DetailAccountingLayout, []);

    const handleSelectionChange = useCallback((name, value, e) => {
        if (props.handleFieldChange) props.handleFieldChange(name, value, e);
    }, [props.handleFieldChange]);

    // Server-loaded dropdowns (AC accounts/departments, FA funds, CA jobs/activity, ID equipment, ID vendor).
    const loadLookups = useCallback(() => {
        const apis = window._env_.REACT_APP_APIS;
        const rp = props.formRefs;
        const ff = props.formFields;
        loadLookupFromDataSource(accessToken, ff, rp, "accountId", `${apis}/${data.AC.GetAcAccounts}`, "id", "name");
        loadLookupFromDataSource(accessToken, ff, rp, "glDepartmentId", `${apis}/${data.AC.GetDepartments}`, "id", "name");
        loadLookupFromDataSource(accessToken, ff, rp, "fundId", `${apis}/${data.FA.GetFafunds}`, "id", "name");
        loadLookupFromDataSource(accessToken, ff, rp, "jobId", `${apis}/${data.CA.GetCaJobs}`, "id", "name");
        loadLookupFromDataSource(accessToken, ff, rp, "activityId", `${apis}/${data.CA.GetCAActivity}`, "id", "name");
        loadLookupFromDataSource(accessToken, ff, rp, "equipmentId", `${apis}/${data.ID.GetEquipments}`, "id", "name");
        loadLookupFromDataSource(accessToken, ff, rp, "vendorId", `${apis}/${data.Common.GetLookup}`, "id", "name", { module: "ID", lookupType: "Vendor", showLookupGrid: false });
    }, [accessToken, props.formRefs, props.formFields]);

    const handleFormLoad = useCallback(() => { setTimeout(loadLookups, 100); }, [loadLookups]);

    const saveAccounting = useCallback(() => {
        if (!hasLine) { notify("Select a detail line first.", "warning", 3000); return; }
        // Post the whole selected line as JSON (mirrors the OM UpdateTransactionDetailsByRowId flow);
        // dates are formatted the way the sample does before sending.
        const payload = {
            ...line,
            transactionDate: line.transactionDate ? GetFormatedDateWithTime(line.transactionDate) : null,
        };
        axios.post(`${window._env_.REACT_APP_APIS}/${ROUTES.UpdateTransactionDetailByRowId}`, payload, {
            headers: {
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
        }).then((res) => {
            notify("Accounting saved.", "success", 3000);
            if (props.onSaved) props.onSaved(res.data);
        }).catch((err) => {
            if (err?.[CONCURRENCY_HANDLED]) return;
            notify(getApiErrorMessage(err, "Failed to save accounting."), "error", 5000);
        });
    }, [hasLine, line, accessToken, props]);

    if (!hasLine) {
        return <div style={{ padding: 16, color: "#888" }}>Select a detail line to view or edit its accounting.</div>;
    }

    return (
        <div>
            <div style={{ textAlign: "right", marginBottom: 4 }}>
                <Button text="Save" type="default" onClick={saveAccounting} disabled={!editable} />
            </div>
            <FormField
                currrentUser={currentUser}
                layoutId="0019d019-6e89-4a0b-f5ac-0019ff001105"
                layoutName="IMTransactionEntryDetailAccountingForm"
                defaultFormSchema={finalLayout}
                formFields={props.formFields}
                setformFields={props.setformFields}
                handleFieldValueChange={handleSelectionChange}
                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                formRefs={props.formRefs}
                onFormLoad={handleFormLoad}
            />
        </div>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(DetailAccountingTab);
